import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { filesize } from "filesize";
import MessagingAPIService from "messaging/api/messaging-api-service";
import Message from "messaging/entities/message/message";
import MessageFileInfo from "messaging/values/attachment/message-file-info";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import { DefaultExtensionType, defaultStyles, FileIcon } from "react-file-icon";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import Session from "users/session/session";

const SentMessageContainer = styled("div")(
  ({ theme }) => ({
    '@container (min-width: 0px)': {
      maxWidth: '90%'
    },
    '@container (min-width: 600px)': {
      maxWidth: '75%'
    },
    '@container (min-width: 800px)': {
      maxWidth: '50%'
    },
    '@container (min-width: 1000px)': {
      maxWidth: '40%'
    },
    margin: theme.spacing(
      2,
      0,
      2,
      'auto'
    ),
    width: 'fit-content'
  }));
const SentMessageInfo = styled("div")(
  ({ theme }) => ({
    alignItems: "flex-start",
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(0.5),
  }));
const MessageSenderName = styled(Typography)(
  () => ({
    margin: 0
  }));
const SentMessageTime = styled(Typography)(
  ({ theme }) => ({
    fontSize: '0.8em',
    marginRight: theme.spacing(1.32),
  }));
const SentMessageAvatar = styled(Avatar)(
  ({ theme }) => ({
    height: theme.spacing(4),
    marginLeft: theme.spacing(2),
    width: theme.spacing(4)
  }));
const SubjectChip = styled(Chip)(
  ({ theme }) => ({
    color: theme.palette.common.white,
    marginBottom: theme.spacing(2),
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }
  }));
const SentMessageAttachment = styled("figure")(
  ({ theme }) => ({
    margin: 0,
    marginTop: theme.spacing(2),
    overflow: "hidden",
    width: "100%",
    "& figcaption": {
      "& .MuiListItemText-primary": {
        fontSize: "0.85em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& .MuiListItemText-secondary": {
        color: "rgba(255, 255, 255, 0.65)",
        fontSize: "0.8em"
      }
    }
  }));
const SentMessageContent = styled('div')(
  ({ theme }) => ({
    backgroundColor: "#3f51b5",
    borderRadius: theme.spacing(2),
    borderBottomRightRadius: 0,
    color: theme.palette.common.white,
    display: "block",
    fontFamily: 'Roboto, sans-serif',
    padding: theme.spacing(3),
    whiteSpace: 'normal',
    "& > p": {
      margin: 0
    },
    "& a": {
      color: theme.palette.common.white
    },
    "& blockquote": {
      borderLeft: '4px solid #ccc',
      color: '#ccc',
      margin: theme.spacing(2),
      paddingLeft: theme.spacing(2)
    },
    "& ul,ol": {
      listStylePosition: "inside",
      margin: 0,
      padding: 0
    }
  }));
const SentMessageLink = styled(Link)(
  ({ theme }) => ({
    color: theme.palette.common.white,
    cursor: "pointer",
    fontSize: theme.spacing(2.2),
    fontWeight: "500",
    textDecoration: "underline",
  }));
const FileTypeIconContainer = styled("div")(
  ({ theme }) => ({
    width: theme.spacing(8),
  }));
const LoadingIndicator = styled(CircularProgress)(
  ({ theme }) => ({
    color: "white",
    display: "flex",
    height: theme.spacing(5),
    width: theme.spacing(5)
  }));
const ImageAttachment = styled("img")(
  () => ({
    width: "100%"
  }));
const DownloadButton = styled(IconButton)(
  ({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    color: theme.palette.common.white,
  }));

type SentMessageProps = {
  session: Readonly<Session>,
  message: Message;
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick: (message: Message, image: MessageFileInfo) => void;
  onDownload: (message: Message, attachment: MessageFileInfo) => void;
};

export const SentMessage = React.forwardRef<HTMLDivElement, SentMessageProps & React.HTMLAttributes<HTMLDivElement>>(
  function SentMessage(props, ref) {
    const {
      session,
      message,
      onImageLoad,
      onImageClick,
      onDownload,
      ...otherProps
    } = props;

    const [isLoadingAttachment, setIsLoadingAttachment] = React.useState(false);
    const [imageData, setImageData] = React.useState<MessageFileInfo[]>([]);

    useEffect(
      () => {
        const hasImagesAttached = message.attachments?.some((attachment: MessageFileInfo) => {
          return attachment.isImage
        });

        if (!hasImagesAttached) return;

        void loadImages(message);
      },
      []
    );

    async function loadImages(message: Message) {
      setIsLoadingAttachment(true);

      try {
        const messagingService = new MessagingAPIService(session);
        const abortController = new AbortController();
        let response = await messagingService.getMessageImages(
          message,
          abortController
        );
        setImageData(response);
      } catch (err) {
        console.error(err);
        enqueueSnackbar(
          "Failed to get message attachment. Please try again.",
          { variant: "error" }
        );
      } finally {
        setIsLoadingAttachment(false);
      }
    }

    function handleAttachmentClick(attachment: MessageFileInfo) {
      if (isLoadingAttachment) return;

      const imageAttachment = imageData.find((image: MessageFileInfo) => image.id.isEqualTo(attachment.id));
      if (attachment.isImage && imageAttachment) {
        onImageClick(message, imageAttachment);
      } else {
        onDownload(message, attachment)
      }
    }

    return (
      <SentMessageContainer ref={ref} {...otherProps}>
        <SentMessageContent>
          <div>
            {(message.subject && message.subject.length > 0) && (
              <SubjectChip label={`RE: ${message.subject}`} size="small" />
            )}
          </div>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
          {message.attachments?.map((attachment: MessageFileInfo) => (
            <SentMessageAttachment key={attachment.id.value}>
              {isLoadingAttachment && <LoadingIndicator disableShrink thickness={2} />}
              {!isLoadingAttachment && (
                <SentMessageLink
                  onClick={() => handleAttachmentClick(attachment)}
                >
                  {attachment.isImage ? (
                    <ImageAttachment
                      alt={attachment.name}
                      src={
                        imageData.find((image: MessageFileInfo) => image.id.isEqualTo(attachment.id))?.dataUri
                      }
                      onLoad={onImageLoad}
                    />
                  ) : (
                    <FileTypeIconContainer>
                      <FileIcon
                        extension={attachment.name.split(".")[1]}
                        {...defaultStyles[attachment.name.split(".")[1] as DefaultExtensionType]}
                      />
                    </FileTypeIconContainer>
                  )}
                </SentMessageLink>
              )}
              <figcaption>
                <ListItem
                  disableGutters
                  secondaryAction={
                    <Tooltip title="Download">
                      <span>
                        <DownloadButton
                          aria-label="download"
                          size="medium"
                          onClick={() => onDownload(message, attachment)}
                        >
                          <DownloadIcon fontSize="medium" />
                        </DownloadButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={attachment.name}
                    secondary={filesize(attachment.fileSize)}
                  />
                </ListItem>
              </figcaption>
            </SentMessageAttachment>
          ))}
        </SentMessageContent>
        <SentMessageInfo>
          <SentMessageTime>
            <time>
              {message.published?.format('MM/DD/YY hh:mm A')}
            </time>
          </SentMessageTime>
          <MessageSenderName>You</MessageSenderName>
          <SentMessageAvatar />
        </SentMessageInfo>
      </SentMessageContainer>
    );
  });
